import { useRef, useState, useCallback, ReactNode, ElementType, RefObject } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { useDraggable } from '@hooks';
import { Button, Text, Caption } from '@components';
import { BUTTON_TYPES, BUTTON_SIZE, TEXT_WEIGHT } from '@types';
import styles from './ModuleToast.module.scss';

export interface ModuleToastProps {
	title: string;
	Icon: ElementType;
	onClose: () => void;
	/** Shown under the title while collapsed — e.g. a status summary. */
	collapsedSubtitle?: ReactNode;
	/** Optional CSS class applied to the root container — use for width/position overrides. */
	className?: string;
	children: ReactNode;
}

/**
 * A draggable, collapsible flyout toast that pins to the bottom-right of the viewport.
 * Drag it by the header; the chevron collapses the body in place. Shares the drag
 * behaviour with ModulePanel via the useDraggable hook, but is otherwise independent —
 * it does not participate in the module dock, resize, or active-module stacking.
 */
export const ModuleToast = (props: ModuleToastProps) => {
	const { title, Icon, onClose, collapsedSubtitle, className, children } = props;

	const headerRef = useRef<HTMLDivElement>(null);
	// anchor: 'bottom' keeps the toast pinned to the bottom-right so it grows upward
	const { elementRef } = useDraggable<HTMLDivElement>(headerRef, { anchor: 'bottom' });
	const [collapsed, setCollapsed] = useState(false);

	const toggleCollapsed = useCallback(() => setCollapsed(prev => !prev), []);

	const containerClass = [styles.container, className].filter(Boolean).join(' ');
	const headerClass = [styles.header, !collapsed ? styles.headerDivider : ''].filter(Boolean).join(' ');

	return (
		<div ref={elementRef as RefObject<HTMLDivElement>} className={containerClass}>
			<div ref={headerRef} className={headerClass}>
				<div className={styles.headerTitle}>
					<Icon fontSize='small' />
					<div className={styles.titleText}>
						<Text weight={TEXT_WEIGHT.SEMI_BOLD} color='var(--txt-heading)'>{title}</Text>
						{collapsed && collapsedSubtitle && <Caption>{collapsedSubtitle}</Caption>}
					</div>
				</div>
				<div className={styles.headerActions}>
					<Button
						iconOnly
						Icon={collapsed ? KeyboardArrowDownIcon : KeyboardArrowUpIcon}
						text={collapsed ? 'Expand' : 'Collapse'}
						onClick={toggleCollapsed}
						type={BUTTON_TYPES.TERTIARY}
						size={BUTTON_SIZE.SMALL}
					/>
					<Button
						iconOnly
						Icon={CloseIcon}
						text='Close'
						onClick={onClose}
						type={BUTTON_TYPES.TERTIARY}
						size={BUTTON_SIZE.SMALL}
					/>
				</div>
			</div>
			{!collapsed && <div className={styles.body}>{children}</div>}
		</div>
	);
};
